/**
 * DataTable — a table built on TanStack Table v8 + Tailwind + a CSS module.
 *
 * PERFORMANCE CONTRACT:
 *  - Geometry flows through CSS variables on the scroll CONTAINER, never per-frame React
 *    state: --utw-w-<id> (width), --utw-left-<id> (sticky offset), --utw-table-min (min table
 *    width), and the drag vars --utw-dx/-dtr/-dop/-dedge-<id>. The var names come from the
 *    shared `cssVars.ts`. Every cell carries `width: var(--utw-w-<id>)` inline, so the body never
 *    reads column.getSize() and a width change never reaches it as a prop.
 *  - The <tbody> is a separate React.memo component (`TableBody`) so its element identity is
 *    STABLE across unrelated re-renders (drag start/end, overlay mount). This is the v8
 *    "performant column resizing" recipe (CSS-var widths + memoized body), adapted to our
 *    container-scoped vars, so a resize/drag causes ZERO body re-renders. React Compiler is
 *    lint-only here, so this manual memo is load-bearing — keep TableBody's props referentially
 *    stable.
 *  - Any per-drag / DragOverlay state renders OUTSIDE the memoized TableBody subtree.
 *  - The body ALWAYS windows its rows with @tanstack/react-virtual — there is one render path,
 *    whatever the row count. A windowed row is `position: absolute` + `translateY(start)`, which
 *    removes it from the table's row box flow, so the table is laid out as `display: grid`/`flex`
 *    (the official TanStack v8 virtualized-rows recipe) and every cell takes its width from its
 *    `--utw-w-<id>` var. Header + totals stay PINNED (sticky <thead>) OUTSIDE the window; sticky
 *    COLUMNS stay INSIDE the windowed rows. The memoized body re-renders on a window move /
 *    load-more / measured-height change (keyed by a `virtualEpoch` token) — NOT on resize/drag,
 *    which don't move the window, so the zero-per-frame-re-render invariant holds.
 *  - SINGLE-DEFER OWNERSHIP: whoever owns `data` owns its deferral. On the list-page path that is
 *    ListTableWithFilters' ONE useDeferredValue; standalone callers pass their data straight in.
 *    Either way the engine consumes `data` AS-IS and builds the row model from it; it MUST NOT call
 *    useDeferredValue on `data`. Deferring an already-deferred value inside a Transition is a
 *    no-op render-wise but
 *    adds churn, and a second independent defer would lag the body a frame behind the
 *    header geometry and the loader/staleness coordination. The virtualizer's
 *    `count`/index space comes from the row model built from this same deferred `data`, so
 *    windowing and time-slicing compose WITHOUT a second defer. The loader/staleness coordination
 *    stays in the chrome (it folds staleness into `isFetching`); DataTable just renders `data` and
 *    surfaces `isFetching`, it does NOT re-derive staleness.
 */
import {
    DndContext,
    DragOverlay,
    type DragCancelEvent,
    type DragEndEvent,
    type DragStartEvent,
    type DropAnimation,
    type Modifier,
} from '@dnd-kit/core';
import { horizontalListSortingStrategy, SortableContext } from '@dnd-kit/sortable';
import {
    flexRender,
    getCoreRowModel,
    getExpandedRowModel,
    getSortedRowModel,
    useReactTable,
    type Cell,
    type ColumnSizingState,
    type ExpandedState,
    type Header,
    type Table,
} from '@tanstack/react-table';
import type { Virtualizer } from '@tanstack/react-virtual';
import { useScrollRestoration } from 'hooks/useScrollRestoration';
import {
    memo,
    useCallback,
    useEffect,
    useId,
    useLayoutEffect,
    useMemo,
    useRef,
    useState,
    type AriaAttributes,
    type CSSProperties,
    type KeyboardEvent,
    type MouseEvent,
    type ReactNode,
    type RefObject,
} from 'react';
import { cn } from 'utils/cn';
import EmptyState from 'components/EmptyState';
import { Skeleton } from 'components/ui/skeleton';
import { t } from 'utils/getTranslations';
import {
    columnDragOpacityVar,
    columnDragTransitionVar,
    columnDragXVar,
    columnDropEdgeVar,
    columnWidthVar,
    stickyLeftVar,
    TABLE_MIN_WIDTH_VAR,
} from './cssVars';
import styles from './dataTable.module.css';
import DataTableColumnResizeHandle from './DataTableColumnResizeHandle';
// Column drag-reorder pieces (sortable header cell, drag preview, reorder hook, mobile gate).
import DataTableColumnDragPreview from './DataTableColumnDragPreview';
import DataTableSortableHeaderCell, { reorderOnSpring } from './DataTableSortableHeaderCell';
import { plainColumnLabel } from './columnMeta';
import type { DataTableColumnMeta, DataTableProps } from './types';
import { EndOfListSentinel } from '../EndOfListSentinel';
import { ExpandPresence, RemeasureRowsContext } from '../ExpandPresence';
import { useDataTableLayout } from './useDataTableLayout';
import { useDataTableReorder } from './useDataTableReorder';
import { readSkeletonRowHeight, rememberRowHeight } from './rememberedRowHeight';
// Row virtualization (@tanstack/react-virtual).
import { DEFAULT_ROW_ESTIMATE, useDataTableRowVirtualizer, virtualEpochToken } from './useDataTableRowVirtualizer';
import { useIsMobile } from 'hooks/useIsMobile';
import { GLIDE } from 'layout/NewDesignLayout/Sidebar/usePinDrag';

// Column drag-reorder is horizontal-only — pin the dragged header's transform to the X axis so it
// tracks the pointer sideways without drifting vertically. Avoids pulling in the separate
// @dnd-kit/modifiers package for a one-line restriction.
const restrictToHorizontalAxis: Modifier = ({ transform }) => ({ ...transform, y: 0 });
const REORDER_MODIFIERS: Modifier[] = [restrictToHorizontalAxis];

// On release the lifted clone in the DragOverlay eases into the dropped column's final
// slot instead of vanishing: in 200ms `ease`, or on the pins' spring (`GLIDE`) where
// `reorderOnSpring` says so. dnd-kit resolves the glide target from the dragged header's SETTLED
// DOM position, which is why the new order is committed synchronously on drop (useDataTableReorder
// → setColumnOrder). During the glide the real column must be hidden so the destination isn't
// painted twice (clone + real cells): dnd-kit's default side-effect only hides the header <th>
// (`active.node`), but our body cells are separate DOM nodes that follow the per-column opacity
// CSS var — so hide both here and restore them (to the idle '1') when the glide finishes.
const REORDER_DROP_ANIMATION: DropAnimation = {
    duration: 200,
    easing: 'ease',
    sideEffects: ({ active }) => {
        const columnId = String(active.id);
        const table = active.node.closest('table');
        const previousHeaderOpacity = active.node.style.opacity;
        active.node.style.opacity = '0';
        table?.style.setProperty(columnDragOpacityVar(columnId), '0');
        return () => {
            active.node.style.opacity = previousHeaderOpacity;
            table?.style.setProperty(columnDragOpacityVar(columnId), '1');
        };
    },
};
const REORDER_DROP_SPRING: DropAnimation = { ...REORDER_DROP_ANIMATION, ...GLIDE };

/** Read a column's layout meta off its v8 def. Defaulted so a column declaring none is inert. */
const readMeta = <TotalsType,>(meta: unknown): DataTableColumnMeta<TotalsType> =>
    (meta ?? {}) as DataTableColumnMeta<TotalsType>;

/**
 * Wires the container's scroll restoration, but ONLY when an id is supplied. `useScrollRestoration`
 * reads React Router location immediately, so calling it unconditionally would require a Router
 * context even when restoration is off — breaking standalone fixtures/tests and making the engine
 * less reusable. Isolating the hook in a child that DataTable renders conditionally keeps the call
 * out of the no-id path entirely (the hook is only invoked when restoration is actually enabled).
 */
const DataTableScrollRestoration = ({
    scopeKey,
    containerRef,
}: {
    scopeKey: string;
    containerRef: RefObject<HTMLDivElement | null>;
}) => {
    const restoreRef = useScrollRestoration(scopeKey);
    useLayoutEffect(() => {
        // The container's own callback ref runs in the commit phase (before layout effects), so
        // containerRef.current is populated here. restoreRef wires the scroll listener; its internal
        // rAF-gated restore reads the node on the next frame.
        restoreRef(containerRef.current);
        return () => restoreRef(null);
    }, [restoreRef, containerRef]);
    return null;
};

// --------------------------------------------------------------------------------------------
//  TableBody — hand-memoized (the v8 performant-body recipe). Element identity stays stable
//  across unrelated parent re-renders; props are kept referentially stable by the parent.
//  Windowing attaches HERE (wraps the rows.map with the virtualizer + spacer rows).
// --------------------------------------------------------------------------------------------
type TableBodyProps<DataType> = {
    // Snapshot the row model + visible columns at the PARENT's render and pass them as props.
    // `useReactTable` returns a STABLE table instance, so a comparator that read
    // `prev.table.getRowModel()` / `next.table.getRowModel()` would see the SAME live instance on
    // both sides — a tautology that never detects a data change and leaves the body frozen on its
    // first (often empty / pre-deferred) render. Comparing these captured references re-renders the
    // body exactly when data / row-state / visibility / order changes.
    rows: ReturnType<Table<DataType>['getRowModel']>['rows'];
    /** Which rows take the zebra's shade, by row index, where the zebra restarts at each group ribbon
     *  (`renderRowLeadIn`); absent, every odd row does. */
    stripedRows: readonly boolean[] | undefined;
    visibleLeafColumns: ReturnType<Table<DataType>['getVisibleLeafColumns']>;
    stickyIds: ReadonlySet<string>;
    lastStickyId: string | undefined;
    onRowClick?: (row: DataType) => void;
    getRowHref?: (row: DataType) => string | undefined;
    onRowNavigate?: (href: string) => void;
    isRowEnabled?: (row: DataType) => boolean;
    renderExpandedContent?: (row: DataType) => ReactNode;
    renderRowLeadIn?: (row: DataType) => ReactNode;
    renderFullRow?: (row: DataType) => ReactNode;
    /** The project-wide LIMITED flag read off a row's DTO. */
    isLimited: (original: DataType) => boolean;
    // Per-row edge accents. Referentially stable from the parent so they never break the body memo.
    rowEdgeColor?: (row: DataType) => string | null | undefined;
    expandedRowEdgeColor?: (row: DataType) => string | null | undefined;
    // Column drag-reorder — a draggable body cell follows its header through the per-column drag CSS
    // vars (shift / dim / drop-edge). Both props are referentially stable from the parent so they
    // never break the body memo; idle ⇒ every var resolves to its no-op default, so the cell is
    // byte-identical to before a drag.
    reorderEnabled: boolean;
    nonDraggableIds: ReadonlySet<string>;
    // Windowing. The body renders ONLY the rows in the virtual window (each absolutely positioned +
    // translateY'd), and the <tbody> takes the virtualizer's total size as its height so the
    // scrollbar reflects ALL rows. The virtualizer instance is referentially stable across renders
    // (TanStack memoizes it), but it mutates internally on scroll — see the memo comparator note: the
    // body MUST re-render on each window move, so `virtualizer` is passed alongside a `virtualEpoch`
    // token that changes when the window or total size does, and the comparator keys on that token.
    virtualizer: Virtualizer<HTMLElement, HTMLTableRowElement>;
    measureRowRef: (node: HTMLTableRowElement | null) => void;
    /** Monotonic token that changes whenever the virtual window or total size changes; the body memo
     *  keys on it so windowed scrolling re-renders the body (and ONLY then). */
    virtualEpoch: string;
};

// Map a column's textAlign to flex justification. A cell is a flex box, so `text-align` does not
// position its content — the justification does.
const justifyFor = (textAlign: CSSProperties['textAlign']): CSSProperties['justifyContent'] | undefined =>
    textAlign === 'right' || textAlign === 'end'
        ? 'flex-end'
        : textAlign === 'center'
          ? 'center'
          : textAlign
            ? 'flex-start'
            : undefined;

/** Shallow value-equality for the column min-floor maps, so re-measuring to the same widths is a
 *  no-op state update (prevents a measure⇄resolve render loop). */
const shallowEqualNumericRecord = (a: Record<string, number>, b: Record<string, number>): boolean => {
    const aKeys = Object.keys(a);
    if (aKeys.length !== Object.keys(b).length) return false;
    return aKeys.every((key) => a[key] === b[key]);
};

/** A single body cell. A draggable cell wraps in the per-column drag CSS vars
 *  (`var(--utw-dx-<id>, none)` etc.) — idle defaults make it byte-identical at rest, and the vars
 *  are written by the SortableHeaderCell on the enclosing <table>, so a drag never re-renders here.
 *  The cell carries its width inline from its `--utw-w-<id>` var and justifies its content (it is a
 *  flex box — the absolutely positioned rows put the table outside the CSS table layout model). */
const renderBodyCell = <DataType,>(
    cell: Cell<DataType, unknown>,
    stickyIds: ReadonlySet<string>,
    lastStickyId: string | undefined,
    reorderEnabled: boolean,
    nonDraggableIds: ReadonlySet<string>,
    /** Composed `box-shadow` for this cell's row-edge accent bar, when it is the row's first or
     *  last cell and the row carries one. */
    edgeShadow: string | undefined
): ReactNode => {
    const column = cell.column;
    const meta = readMeta(column.columnDef.meta);
    const isSticky = stickyIds.has(column.id);
    // A draggable body cell is one whose column can be dragged (reorder on, not in the
    // sticky/fixed/locked non-draggable set). Such cells are never sticky (the sticky prefix is
    // non-draggable by construction), so the drag and sticky styles never collide.
    const isDraggable = reorderEnabled && !nonDraggableIds.has(column.id);
    const dragStyle: CSSProperties = isDraggable
        ? {
              transform: `var(${columnDragXVar(column.id)}, none)`,
              transition: `var(${columnDragTransitionVar(column.id)}, none)`,
              opacity: `var(${columnDragOpacityVar(column.id)}, 1)`,
              // Thin grey insertion line on the column the pointer is over; transparent idle
              // fallback ⇒ byte-identical at rest.
              boxShadow: `var(${columnDropEdgeVar(column.id)}, 0 0 0 0 transparent)`,
          }
        : {};
    return (
        <td
            key={cell.id}
            role="cell"
            data-col-id={column.id}
            data-sticky={isSticky || undefined}
            data-sticky-last={(isSticky && column.id === lastStickyId) || undefined}
            style={{
                width: `var(${columnWidthVar(column.id)})`,
                ...(meta.textAlign ? { textAlign: meta.textAlign } : {}),
                ...(meta.padding !== undefined ? { padding: meta.padding } : {}),
                justifyContent: justifyFor(meta.textAlign),
                ...(isSticky ? { left: `var(${stickyLeftVar(column.id)})` } : {}),
                ...dragStyle,
                // A row-edge accent is only ever set on the FIRST / LAST cell, and a table that
                // paints edge bars does not enable reorder — so this never has to compose with
                // `dragStyle`'s own boxShadow (drop-edge line).
                ...(edgeShadow ? { boxShadow: edgeShadow } : {}),
            }}
        >
            {flexRender(column.columnDef.cell, cell.getContext())}
        </td>
    );
};

// What a click on a row may land on and still be the control's own click.
const ROW_CONTROLS =
    'a, button, input, label, select, textarea, summary, [role="button"], [role="checkbox"], ' +
    '[role="switch"], [role="menuitem"], [role="combobox"], [role="tab"], [data-expanded-content], [data-lead-in]';

/** A pointer event on the row itself: not on a control in it, not from a portal opened from it. */
function isOnRowItself(event: MouseEvent<HTMLTableRowElement>): boolean {
    const row = event.currentTarget;
    const target = event.target as Element;
    // React bubbles a portalled menu's click up to the row; the DOM tree does not hold it.
    if (!row.contains(target)) return false;
    const control = target.closest(ROW_CONTROLS);
    if (control && row.contains(control)) return false;
    // A disabled kit button is `pointer-events: none`, so a press on it lands on what holds it.
    const { clientX: x, clientY: y } = event;
    return !Array.from(target.querySelectorAll(ROW_CONTROLS)).some((inner) => {
        const rect = inner.getBoundingClientRect();
        return x >= rect.left && x < rect.right && y >= rect.top && y < rect.bottom;
    });
}

/** A click that should open the row's record: on the row itself, and not the end of a text selection in it. */
function isRowOpeningClick(event: MouseEvent<HTMLTableRowElement>): boolean {
    if (!isOnRowItself(event)) return false;
    const selection = window.getSelection();
    // A selection elsewhere on the page did not come from this click.
    return !selection || selection.isCollapsed || !selection.containsNode(event.currentTarget, true);
}

const openInNewTab = (href: string) => window.open(href, '_blank', 'noopener');

type BodyRowProps<DataType> = Pick<
    TableBodyProps<DataType>,
    | 'visibleLeafColumns'
    | 'stickyIds'
    | 'lastStickyId'
    | 'onRowClick'
    | 'getRowHref'
    | 'onRowNavigate'
    | 'isRowEnabled'
    | 'renderExpandedContent'
    | 'renderRowLeadIn'
    | 'renderFullRow'
    | 'isLimited'
    | 'rowEdgeColor'
    | 'expandedRowEdgeColor'
    | 'reorderEnabled'
    | 'nonDraggableIds'
    | 'measureRowRef'
> & {
    row: TableBodyProps<DataType>['rows'][number];
    virtualIndex: number;
    start: number;
    /** The zebra's shaded half (see `stripedRows`). */
    striped: boolean;
    // Read off the table's state by the parent, so a toggle reaches the memoized row as a changed prop.
    selected: boolean;
    expanded: boolean;
};

/**
 * One keyed <tr> (+ its optional lead-in and expanded blocks), memoized on its own props: a scroll that
 * moves the virtual window re-renders the body, but a row that stays in the window keeps every prop, so
 * only the rows entering it render their cells. `selected` / `expanded` come in as props because the row
 * object itself does not change when they do. Selection shading that a column draws itself still
 * follows a column rebuild (`visibleLeafColumns`), as it always has.
 *
 * The row is absolutely positioned and shifted by the virtual item's `start`; it also carries
 * `data-index` (read by virtualizer.measureElement) and the measure ref so its REAL height (wrapping
 * cells, edge bars, expanded content) replaces the estimate.
 */
const BodyRowInner = <DataType,>({
    row,
    virtualIndex,
    start,
    striped,
    selected,
    expanded,
    visibleLeafColumns,
    stickyIds,
    lastStickyId,
    onRowClick,
    getRowHref,
    onRowNavigate,
    isRowEnabled,
    renderExpandedContent,
    renderRowLeadIn,
    renderFullRow,
    isLimited,
    rowEdgeColor,
    expandedRowEdgeColor,
    reorderEnabled,
    nonDraggableIds,
    measureRowRef,
}: BodyRowProps<DataType>) => {
    const visibleColumnCount = visibleLeafColumns.length;
    const original = row.original;
    const disabled = isRowEnabled ? !isRowEnabled(original) : false;
    const href = !disabled && onRowNavigate ? getRowHref?.(original) : undefined;
    const clickable = !!onRowClick && !disabled && !href;
    // A press on a control released off it clicks their common ancestor, the row's own: the press decides.
    const pressedControl = useRef(false);
    const limited = isLimited(original);
    // Per-edge accent: a 2px inset bar painted via box-shadow on the row's first / last cell.
    // Box-shadow leaves the cell's own background intact and sits above the sticky-column /
    // hover backgrounds, so it degrades cleanly when a row has no accent.
    const edgeColor = rowEdgeColor ? rowEdgeColor(original) : null;
    const fullRow = renderFullRow ? renderFullRow(original) : null;
    const visibleCells = row.getVisibleCells();
    const lastCellIndex = visibleCells.length - 1;
    // `padding: 0`, as the lead-in's: the block lays itself out against the columns' widths.
    const cells = fullRow ? (
        <td role="cell" data-full-row="true" colSpan={visibleColumnCount} style={{ width: '100%', padding: 0 }}>
            {fullRow}
        </td>
    ) : (
        visibleCells.map((cell, index) =>
            renderBodyCell(
                cell,
                stickyIds,
                lastStickyId,
                reorderEnabled,
                nonDraggableIds,
                edgeColor && index === 0
                    ? `inset 2px 0 0 0 ${edgeColor}`
                    : edgeColor && index === lastCellIndex
                      ? `inset -2px 0 0 0 ${edgeColor}`
                      : undefined
            )
        )
    );
    // The expanded block sits beneath the parent row and would otherwise read as un-bordered.
    // `expandedRowEdgeColor` lets a caller paint it differently from the row (e.g. border the
    // group row when any member is the user's, but the expanded block only when every member is).
    const expandedEdgeColor = expandedRowEdgeColor ? expandedRowEdgeColor(original) : edgeColor;
    const expandedSubRow = renderExpandedContent ? (
        <ExpandPresence open={expanded}>
            {(phase) => (
                // No padding override: the expanded block keeps the shared cell rule's 16px inset,
                // so its content lines up with the row above it.
                <td
                    role="cell"
                    colSpan={visibleColumnCount}
                    data-expanded-content
                    data-expand-phase={phase}
                    style={{
                        width: '100%',
                        ...(expandedEdgeColor
                            ? {
                                  boxShadow: `inset 2px 0 0 0 ${expandedEdgeColor}, inset -2px 0 0 0 ${expandedEdgeColor}`,
                              }
                            : {}),
                    }}
                >
                    {renderExpandedContent(original)}
                </td>
            )}
        </ExpandPresence>
    ) : null;
    // A group ribbon (or any full-width block a caller wants above the row) rides on the SAME
    // <tr> as a leading flex line, exactly as the expanded sub-row rides on it as a trailing one.
    // That keeps ONE measured node per row: the virtualizer sees a taller row rather than an extra
    // index, so the row model, `data-index` and the expanded model all stay in step, where a
    // separate <tr> per group would desynchronise them. `padding: 0` because the block paints edge
    // to edge; the cell's own bottom border is the ribbon's own.
    const leadIn = renderRowLeadIn ? renderRowLeadIn(original) : null;
    const leadInCell = leadIn ? (
        <td
            role="cell"
            data-lead-in="true"
            colSpan={visibleColumnCount}
            style={{ width: '100%', padding: 0 }}
            // The block sits inside the row's own <tr>, which carries the row click and its
            // keyboard equivalent — so without this a group ribbon would open the first row of
            // its group when clicked.
            onClick={(event) => event.stopPropagation()}
        >
            {leadIn}
        </td>
    ) : null;

    // The parent row + its expanded sub-row participate in item sizing as ONE measured row: the
    // virtualizer measures the wrapper <tr> height, which spans the data row AND the expanded
    // block (rendered inside the same <tr> as a second flex line). Keeping them in one measured
    // node means an expanded row simply has a larger measured height — no extra index in the
    // virtual list, so getExpandedRowModel stays correct.
    return (
        <tr
            data-index={virtualIndex}
            ref={measureRowRef}
            style={{
                transform: `translateY(${start}px)`,
                flexWrap: expandedSubRow || leadInCell ? 'wrap' : undefined,
            }}
            role="row"
            data-stripe={striped || undefined}
            data-clickable={clickable || !!href || undefined}
            data-disabled={disabled || undefined}
            data-selected={selected || undefined}
            data-limited={limited || undefined}
            data-expanded={expanded || undefined}
            onClick={
                href
                    ? (event: MouseEvent<HTMLTableRowElement>) => {
                          if (pressedControl.current || !isRowOpeningClick(event)) return;
                          if (event.ctrlKey || event.metaKey || event.shiftKey) openInNewTab(href);
                          else onRowNavigate?.(href);
                      }
                    : clickable
                      ? () => onRowClick?.(original)
                      : undefined
            }
            onAuxClick={
                href
                    ? (event: MouseEvent<HTMLTableRowElement>) => {
                          if (event.button === 1 && !pressedControl.current && isRowOpeningClick(event))
                              openInNewTab(href);
                      }
                    : undefined
            }
            // Shift+mousedown would stretch the page's text selection to the row, and the click would then
            // read as the end of a selection instead of a Shift-click.
            onMouseDown={
                href
                    ? (event: MouseEvent<HTMLTableRowElement>) => {
                          pressedControl.current = !isOnRowItself(event);
                          if (event.shiftKey && !pressedControl.current) event.preventDefault();
                      }
                    : undefined
            }
            // Keyboard parity for onRowClick rows: focusable + Enter/Space activation (a11y). A row
            // that opens its href is no Tab stop: the record's own link in a cell is the keyboard path.
            tabIndex={clickable ? 0 : undefined}
            onKeyDown={
                clickable
                    ? (event: KeyboardEvent<HTMLTableRowElement>) => {
                          if (event.key === 'Enter' || event.key === ' ') {
                              event.preventDefault();
                              onRowClick?.(original);
                          }
                      }
                    : undefined
            }
        >
            {leadInCell}
            {cells}
            {expandedSubRow}
        </tr>
    );
};

const BodyRow = memo(BodyRowInner) as typeof BodyRowInner;

const TableBodyInner = <DataType,>({
    rows,
    stripedRows,
    visibleLeafColumns,
    stickyIds,
    lastStickyId,
    onRowClick,
    getRowHref,
    onRowNavigate,
    isRowEnabled,
    renderExpandedContent,
    renderRowLeadIn,
    renderFullRow,
    isLimited,
    rowEdgeColor,
    expandedRowEdgeColor,
    reorderEnabled,
    nonDraggableIds,
    virtualizer,
    measureRowRef,
}: TableBodyProps<DataType>) => (
    // The windowing surface. The <tbody> height is the virtualizer's TOTAL size so the scrollbar
    // reflects ALL rows while only ~viewport rows exist in the DOM; the rows inside are
    // absolutely positioned + translated to their `start` offset.
    <tbody className={styles.tbody} role="rowgroup" style={{ height: `${virtualizer.getTotalSize()}px` }}>
        {virtualizer.getVirtualItems().map((virtualItem) => {
            const row = rows[virtualItem.index];
            if (!row) return null;
            return (
                <BodyRow<DataType>
                    key={row.id}
                    row={row}
                    virtualIndex={virtualItem.index}
                    start={virtualItem.start}
                    striped={stripedRows ? !!stripedRows[virtualItem.index] : virtualItem.index % 2 === 1}
                    selected={row.getIsSelected()}
                    expanded={row.getIsExpanded()}
                    visibleLeafColumns={visibleLeafColumns}
                    stickyIds={stickyIds}
                    lastStickyId={lastStickyId}
                    onRowClick={onRowClick}
                    getRowHref={getRowHref}
                    onRowNavigate={onRowNavigate}
                    isRowEnabled={isRowEnabled}
                    renderExpandedContent={renderExpandedContent}
                    renderRowLeadIn={renderRowLeadIn}
                    renderFullRow={renderFullRow}
                    isLimited={isLimited}
                    rowEdgeColor={rowEdgeColor}
                    expandedRowEdgeColor={expandedRowEdgeColor}
                    reorderEnabled={reorderEnabled}
                    nonDraggableIds={nonDraggableIds}
                    measureRowRef={measureRowRef}
                />
            );
        })}
    </tbody>
);

/**
 * Memoized table body — the v8 "performant column resizing" recipe (CSS-var widths + a memoized
 * body) adapted to our container-scoped vars. Element identity stays STABLE across unrelated
 * parent re-renders (resize, drag start/end, overlay mount); geometry never reaches the body as
 * props — it flows through CSS variables on the container — so a resize/drag causes ZERO body
 * re-renders. React Compiler is lint-only here, so this manual memo is load-bearing.
 *
 * The custom comparator compares the body's ACTUAL inputs: the row-model + visible-column
 * references SNAPSHOTTED at the parent's render and passed in as `rows` / `visibleLeafColumns` props
 * (both memoized inside TanStack, so they only change on real data / row-state / visibility / order
 * changes) and the other render-affecting props. CRITICAL: it must compare those captured props, NOT
 * `prev.table.getRowModel()` vs `next.table.getRowModel()` — `useReactTable` returns a STABLE
 * instance, so reading the live model on both sides is a tautology that never detects a data change
 * (it freezes the body empty until some other prop happens to change). Column SIZING is deliberately
 * ignored (flows through CSS vars, never the body). Returning true skips the re-render.
 *
 * Callers MUST pass referentially-stable `onRowClick` / `getRowHref` / `onRowNavigate` / `isRowEnabled` /
 * `renderExpandedContent` / `renderRowLeadIn` / `renderFullRow`
 * (ListTableWithFilters already does) — an inline closure would churn and defeat the memo.
 * Generic identity is preserved with a cast (React.memo erases the type parameter).
 */
const TableBody = memo(TableBodyInner, (prev, next) => {
    if (
        prev.stickyIds !== next.stickyIds ||
        prev.lastStickyId !== next.lastStickyId ||
        prev.onRowClick !== next.onRowClick ||
        prev.getRowHref !== next.getRowHref ||
        prev.onRowNavigate !== next.onRowNavigate ||
        prev.isRowEnabled !== next.isRowEnabled ||
        prev.renderExpandedContent !== next.renderExpandedContent ||
        prev.renderRowLeadIn !== next.renderRowLeadIn ||
        prev.renderFullRow !== next.renderFullRow ||
        prev.stripedRows !== next.stripedRows ||
        prev.isLimited !== next.isLimited ||
        prev.rowEdgeColor !== next.rowEdgeColor ||
        prev.expandedRowEdgeColor !== next.expandedRowEdgeColor ||
        // Both are referentially stable from the parent; they only change when reorder turns on/off
        // or the non-draggable set changes (mode/visibility/sticky change) — never per drag frame,
        // so a live drag (CSS-var only) still skips the body re-render entirely.
        prev.reorderEnabled !== next.reorderEnabled ||
        prev.nonDraggableIds !== next.nonDraggableIds ||
        // Windowing identity. `measureRowRef` is referentially stable; `virtualizer` is too (TanStack
        // memoizes the instance), but it MUTATES internally as the user scrolls — its rendered window
        // + total size change without a new instance reference. `virtualEpoch` encodes (first visible
        // index · count · total size), so the body re-renders EXACTLY when the window moves, the row
        // count changes (load-more), or a measured height shifts the spacer — and NOT otherwise.
        // CRITICAL for the zero-per-frame-re-render invariant: a resize/drag does NOT move the
        // window, so `virtualEpoch` is unchanged and the body still skips the re-render entirely;
        // only genuine SCROLL (the whole point of windowing) re-renders the body, and of its rows only
        // those entering the window (each row is memoized on its own props, see BodyRow).
        prev.virtualizer !== next.virtualizer ||
        prev.measureRowRef !== next.measureRowRef ||
        prev.virtualEpoch !== next.virtualEpoch
    ) {
        return false;
    }
    // Row model + visible columns are memoized inside TanStack; reference equality ⇒ no body
    // change. (Sorting is server-side so the row model only changes when `data` changes.)
    //
    // This is what BOUNDS the column-reorder DROP. Mid-drag NOTHING here changes — the visual move
    // flows entirely through the container CSS vars (--utw-dx/-dop/-dedge-*), so the body element
    // identity is stable and there are ZERO per-frame body re-renders. Only on DROP does
    // `columnOrder` change, which makes v8 return a NEW `getVisibleLeafColumns()` reference; the
    // comparator then returns false and the body re-renders EXACTLY ONCE. That single pass is
    // bounded: every <tr>/<td> is keyed by row.id / cell.id (cell.id embeds the column id), so React
    // reconciles the reorder by MOVING the existing keyed DOM nodes into their new positions rather
    // than recreating them — flexRender re-runs for the moved cells, but no row/cell subtree is torn
    // down and rebuilt. The DragOverlay clone + per-drag state live OUTSIDE this memo (component
    // render scope), so pickup/drop never recomputes the body for the overlay alone.
    // Compare the row-model + visible-column snapshots captured at the PARENT's render (passed as
    // props), NOT `prev.table.getRowModel()` / `next.table.getRowModel()`. The table instance is
    // STABLE across renders, so the live-model form would read the same model on both sides — a
    // tautology that never sees a data change and freezes the body on its first render. v8 returns a
    // new `rows` array when `data` changes and a new `getVisibleLeafColumns()` array on
    // visibility/order changes, so these references flip exactly when the body must re-render.
    return prev.rows === next.rows && prev.visibleLeafColumns === next.visibleLeafColumns;
}) as typeof TableBodyInner;

// Skeleton row count shown while the initial page (or a new filter/sort combination) loads with no
// rows yet — enough to fill the table's visible area, so the loading state reads as a full table.
const SKELETON_LOADING_ROWS = 10;

/** How long the totals row takes to slide in or out: the length of its keyframes in the stylesheet. */
const TOTALS_SLIDE_MS = 200;

/**
 * Loading skeleton body, standing in for the rows that have not arrived yet. The chrome withholds
 * `data` while a filter/sort combination with no cache entry loads (`tableLoadingProps` → blocking
 * `isLoading`), so without this the table would show an empty header-only body on every cold load /
 * filter change. Each cell takes its column's `--utw-w-<id>` width like a real cell, so the skeleton
 * lines up with the header; the rows carry no `data-index`, so they stay in flow instead of being
 * positioned by the virtualizer. One placeholder per visible column, so each row reads as a table row.
 *
 * The ROW carries the height, at what this list's rows last measured (see `rememberedRowHeight`), so
 * the loaded rows land where the skeleton drew them; a list that has never measured its rows draws
 * the default. Each cell centres a 40px bar tinted with the text colour at 11%, the app's placeholder
 * shade, so the bar looks the same whatever height the row takes. The height is read once per mount:
 * a skeleton that stays up does not resize under the user.
 */
const DataTableSkeletonBody = ({
    columnIds,
    rowCount,
    listId,
}: {
    columnIds: string[];
    rowCount: number;
    listId: string | undefined;
}) => {
    const [rowHeight] = useState(() => readSkeletonRowHeight(listId));
    return (
        <tbody className={styles.tbody} role="rowgroup">
            {Array.from({ length: rowCount }).map((_, rowIndex) => (
                // oxlint-disable-next-line react-doctor/no-array-index-as-key -- static skeleton placeholders never reorder
                <tr key={`skeleton-row-${rowIndex}`} role="row" style={{ height: rowHeight }}>
                    {columnIds.map((columnId) => (
                        <td key={columnId} role="cell" style={{ width: `var(${columnWidthVar(columnId)})` }}>
                            <Skeleton className="bg-fg/11 h-10 w-full" />
                        </td>
                    ))}
                </tr>
            ))}
        </tbody>
    );
};

// --------------------------------------------------------------------------------------------
//  DataTable engine
// --------------------------------------------------------------------------------------------
export const DataTable = <DataType, TotalsType = unknown>(props: DataTableProps<DataType, TotalsType>) => {
    const {
        data,
        columns,
        getRowId,
        columnConfigId,
        enableColumnReorder = false,
        enableColumnResize = false,
        sorting,
        onSortingChange,
        columnVisibility,
        onColumnVisibilityChange,
        columnOrder,
        onColumnOrderChange,
        onRowClick,
        getRowHref,
        onRowNavigate,
        isRowEnabled,
        rowEdgeColor,
        expandedRowEdgeColor,
        rowSelection,
        onRowSelectionChange,
        expanded,
        onExpandedChange,
        renderExpandedContent,
        renderRowLeadIn,
        renderFullRow,
        renderStickyLeadIn,
        totals,
        showTotalsRow = false,
        isLoadingTotals = false,
        animateTotalsRow = false,
        isLoading = false,
        isFetching = false,
        skeletonRows = SKELETON_LOADING_ROWS,
        skipHeader = false,
        ariaLabel,
        emptyState,
        // Pagination — infinite scroll off the engine's own scroll container.
        onLoadMore,
        hasNextPage = false,
        isLoadingMore = false,
        // Windowing.
        scrollRestorationId,
        estimatedRowHeight = DEFAULT_ROW_ESTIMATE,
        className,
        onScrollElementChange,
    } = props;

    // Resize and reorder commit through the persisted layout entry, so they are only offered when
    // there is one to write to; column geometry itself is resolved for every table.
    const hasLayoutPersistence = !!columnConfigId;

    // Selection / expansion are controlled when the owner supplies state; otherwise the table
    // owns them internally (initialised empty). columnSizing stays an empty constant: the column
    // layout does NOT use v8's built-in px sizing — our weighted flex-reflow + ratio persistence
    // owns geometry and writes the --utw-* CSS vars on the container (see useDataTableLayout).
    const internalColumnSizing = useMemo<ColumnSizingState>(() => ({}), []);

    // react-hooks-js/incompatible-library: TanStack Table v8's `useReactTable` returns an instance
    // whose methods are functions the React Compiler cannot memoize, so it skips compiling this
    // component. This is inherent to the v8 API (not refactorable). The performance contract here
    // does NOT rely on the compiler: geometry flows through CSS vars and the body is HAND-memoized.
    // eslint-disable-next-line react-hooks-js/incompatible-library -- TanStack Table v8 returns non-memoizable functions; manual memoization is load-bearing here
    const table = useReactTable<DataType>({
        // Consumed AS-IS — no internal useDeferredValue here, so on the list-page path this stays
        // the value ListTableWithFilters already deferred. The row model built from it is what the
        // virtualizer must window over (the same rows).
        data,
        columns,
        getRowId,
        // Server-side sort: the indicator reflects `sorting`, react does not reorder rows.
        manualSorting: true,
        // Every sortable header cycles ASC -> DESC -> cleared. Left to itself, TanStack picks the
        // first direction from `getAutoSortDir()` — the TYPE of the first sorted row's value — which
        // is unstable under manualSorting: sorting asc can surface a null/string first value,
        // flipping the first direction to 'asc' so the removal branch (`isSorted !== firstSortDir`)
        // never fires and the third click wrongly re-sorts instead of clearing. Pinning
        // `sortDescFirst: false` makes the first direction deterministic (asc), and
        // `enableSortingRemoval` (default true, set explicitly) restores the clearing step.
        sortDescFirst: false,
        enableSortingRemoval: true,
        // Single-column sort only: the chrome's sort bridge (ListTableWithFilters →
        // convertSortToString) serializes just one rule, and the server sort is single-column. v8
        // would otherwise APPEND a column on shift-click (multi-sort), leaving the first rule in
        // place so the click looks ignored. Disabling multi-sort keeps SortingState single-entry.
        enableMultiSort: false,
        // Controlled state (owner = WithFilters wrapper). Absent slots fall back to instance state.
        state: {
            ...(sorting !== undefined ? { sorting } : {}),
            ...(columnVisibility !== undefined ? { columnVisibility } : {}),
            ...(columnOrder !== undefined ? { columnOrder } : {}),
            ...(rowSelection !== undefined ? { rowSelection } : {}),
            ...(expanded !== undefined ? { expanded: expanded as ExpandedState } : {}),
            columnSizing: internalColumnSizing,
        },
        onSortingChange,
        onColumnVisibilityChange,
        // v8's onColumnOrderChange is an OnChangeFn; our owner takes the resolved array. Bridge
        // it by resolving the updater against the current order before forwarding.
        onColumnOrderChange: onColumnOrderChange
            ? (updater) => onColumnOrderChange(typeof updater === 'function' ? updater(columnOrder ?? []) : updater)
            : undefined,
        onRowSelectionChange,
        // Only forward when the owner controls expansion: passing the key as `undefined` would
        // shadow v8's default state updater (options are merged by spread), leaving
        // `table.setExpanded` a no-op so uncontrolled rows could never expand.
        ...(onExpandedChange !== undefined ? { onExpandedChange: onExpandedChange as never } : {}),
        enableRowSelection: true,
        getRowCanExpand: () => !!renderExpandedContent,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getExpandedRowModel: getExpandedRowModel(),
        // NO columnResizeMode — v8's built-in px resize is deliberately unused; the column layout
        // (useDataTableLayout) owns geometry via CSS vars on the container.
    });

    const visibleLeafColumns = table.getVisibleLeafColumns();
    const headerGroups = table.getHeaderGroups();
    const tableId = useId();

    // Column layout — measures the container, resolves runtime widths through
    // resolveColumnWidths, writes the --utw-* CSS vars on the container (zero React state per
    // frame), and owns the sticky prefix + drag-resize handlers.
    // Per-column minimum-width floor (px) the layout must honour beyond each column's own minPx, so
    // content the weight-derived width can't fit is never clipped: the header title (one line, incl.
    // the hover drag grip) and the totals value. Measured from the rendered DOM (see the layout
    // effect below) and fed to the layout hook as `extraMinPx`; recomputed when the inputs change
    // (so e.g. toggling totals on/off reflows the columns).
    const [columnMinFloorPx, setColumnMinFloorPx] = useState<Record<string, number>>(() => ({}));

    const {
        containerRef: layoutContainerRef,
        stickyIds,
        lastStickyId,
        onResizeStart,
        onResizeMove,
        onResizeEnd,
        onResizeReset,
        onResizeBy,
        resizeValues,
    } = useDataTableLayout<DataType>({ columnConfigId, visibleLeafColumns, extraMinPx: columnMinFloorPx });

    const isLimited = useMemo(() => hasLimitedVisibility as (original: DataType) => boolean, []);

    // ========================================================================================
    //  ROW VIRTUALIZATION (@tanstack/react-virtual)
    //  ----------------------------------------------------------------------------------------
    //  DOM-MODEL DECISION. The body windows by rendering only ~viewport rows, each
    //  `position: absolute` + `transform: translateY(start)` (the official TanStack v8
    //  virtualized-rows recipe). An absolutely positioned <tr> is REMOVED from the table's row
    //  box flow, so the CSS table layout model (`table-layout`, `<colgroup>`) cannot size those
    //  cells — which is why the official recipe lays the table out as `display: grid`
    //  (table/thead/tbody) + `display: flex` (rows/cells). Every cell takes its width from its
    //  `--utw-w-<id>` variable (see dataTable.module.css). The body windows for EVERY row count:
    //  a second, in-flow render path for small tables would switch the box model, the header
    //  pinning and the alignment mechanism under the user the moment load-more crossed a
    //  threshold. Header + totals stay PINNED (sticky <thead>) OUTSIDE the window; sticky COLUMNS
    //  stay INSIDE the windowed rows via `--utw-left-<id>` (data-sticky cells).
    //
    //  WHY @tanstack/react-virtual over react-window / react-virtuoso: it is the SAME family as the
    //  v8 table engine and shares its measurement model — driven by `count` + `estimateSize`,
    //  emitting virtual items whose `index` maps straight into `table.getRowModel().rows`, with one
    //  `measureElement` ref for variable heights. The header/totals can stay sticky in the SAME
    //  container scroller (react-window's inner fixed-height list traps the sticky header — the
    //  documented bug; react-virtuoso owns its own scroller + DOM, which would fight our
    //  container-scoped `--utw-*` geometry and the resize/reorder pipeline). See
    //  useDataTableRowVirtualizer.ts for the full justification.
    //
    //  COMPOSITION WITH TIME-SLICING: the virtualizer's `count`/index space comes from the row
    //  model built from `data` exactly as the caller supplied it, so windowing + time-slicing
    //  compose WITHOUT a second defer.
    // ========================================================================================
    const rows = table.getRowModel().rows;

    // The zebra restarts at each group ribbon, so every group opens on an unshaded row whatever the
    // length of the group above it. Only rows that carry ribbons need the walk; the others shade by index.
    const stripedRows = useMemo(() => {
        if (!renderRowLeadIn) return undefined;
        let positionInGroup = 0;
        return rows.map((row) => {
            if (renderRowLeadIn(row.original)) positionInGroup = 0;
            return positionInGroup++ % 2 === 1;
        });
    }, [rows, renderRowLeadIn]);

    // Loading with nothing to show yet: render the skeleton body rather than an empty header-only
    // table. Covers BOTH the blocking cold load (`isLoading`, no cache entry)
    // AND the case where rows exist upstream but haven't reached this render yet (`isFetching` folds
    // in the chrome's deferred row-population lag / tab-switch refetch, where `isLoading` is false
    // but `data` is still empty) — without this the table flashes an empty body until the rows land.
    // When cached rows ARE present (load-more / revalidation), `data.length > 0` keeps them on screen
    // with the veil overlay instead, so this only ever fills a genuinely empty table.
    const showLoadingSkeleton = (isLoading || isFetching) && data.length === 0;

    // Loading has finished and there is nothing to draw. Without this the table falls through to a
    // `<tbody>` with no rows under a full `<thead>` inside a `height: 100%` frame — the header row
    // over a pane of nothing. Rendered below, in place of the whole table.
    const showEmptyState = data.length === 0 && !showLoadingSkeleton;

    // The scroll container DOM node: the virtualizer's vertical scroll element AND the
    // column-layout measurement root AND the scroll-restoration target. We keep our own node ref
    // and compose it with the layout callback ref + the scroll-restoration callback ref below (the
    // layout hook keeps its own internal node ref; this one is for the virtualizer's
    // `getScrollElement`).
    const scrollContainerNodeRef = useRef<HTMLDivElement | null>(null);
    // The SAME node, in state, for the infinite-scroll observer: an IntersectionObserver root is read
    // when the observer is built, and a ref still holds null on the first render — arming against
    // `null` would silently fall back to the viewport root, where the container's own clipping makes
    // the prefetch margin unreachable. Set from the container callback ref below, which React runs in
    // the same commit as the column-layout measurement, so this costs no extra render pass.
    // oxlint-disable-next-line react-doctor/rerender-state-only-in-handlers -- must be state, not a ref: the observer is built in an effect keyed on this node, and a ref mutation would not re-run it
    const [scrollContainerNode, setScrollContainerNode] = useState<HTMLDivElement | null>(null);
    // Opt-in scroll restoration for the internal vertical scroll container is wired by the
    // <DataTableScrollRestoration> child below, rendered ONLY when `scrollRestorationId` is set — so
    // the router-reading `useScrollRestoration` hook is never invoked on the no-id path (the engine
    // stays usable outside a Router). With a windowed body the spacer (<tbody> height) is the
    // virtualizer's total size, so the container is tall enough to reach the saved offset once
    // estimates exist for every row — and useScrollRestoration already WAITS across rAFs until
    // `scrollHeight - clientHeight >= target`, i.e. it restores AFTER the layout settles, never before.
    const getScrollElement = useCallback(() => scrollContainerNodeRef.current, []);

    // Stable per-row key so measured heights survive sort / load-more (key by id, not index).
    // Falls back to the index when no row model row exists yet.
    const getVirtualItemKey = useCallback((index: number) => rows[index]?.id ?? index, [rows]);

    const { virtualizer, measureRowRef, rowEstimate } = useDataTableRowVirtualizer({
        rowCount: rows.length,
        getScrollElement,
        estimateRowHeight: estimatedRowHeight,
        getItemKey: getVirtualItemKey,
    });

    // Once the rendered rows have re-tuned the estimate away from its seed, it is what this list's rows
    // measure: keep it for the list's next loading skeleton. An estimate still at the seed says nothing
    // about the rows, so it is never written.
    useEffect(() => {
        if (rows.length > 0 && rowEstimate !== estimatedRowHeight) rememberRowHeight(columnConfigId, rowEstimate);
    }, [columnConfigId, rows.length, rowEstimate, estimatedRowHeight]);

    // A monotonic token the memoized body keys on so it re-renders EXACTLY when the window moves,
    // the row count changes (load-more), or a measured height shifts the spacer — and not on an
    // unrelated parent re-render. Cheap to compute; read off the (mutating) virtualizer instance.
    const virtualItems = virtualizer.getVirtualItems();
    const virtualEpoch = virtualEpochToken(virtualItems[0]?.index, virtualItems.length, virtualizer.getTotalSize());

    // The row the frozen lead-in block follows: the virtualizer's own `range.startIndex`, which is
    // the first row the scroll offset has NOT gone past — its window adds the overscan rows around
    // that, so this is the topmost row actually on screen. Read rather than re-derived: the
    // `getVirtualItems()` call above is what computes and assigns it, and it is null exactly when
    // that call returns no items. Memoized on the row, so the block re-renders when the topmost row
    // changes rather than on every scroll frame within one group.
    const topVisibleRow = rows[virtualizer.range?.startIndex ?? -1];
    const stickyLeadIn = useMemo(
        () => (renderStickyLeadIn && topVisibleRow ? renderStickyLeadIn(topVisibleRow.original) : null),
        [renderStickyLeadIn, topVisibleRow]
    );

    // The sticky head covers whatever scrolls under it. Its height, totals row included, is written on the
    // scroll container, and the controls in the rows keep that much clear above themselves (the CSS module's
    // `scroll-margin-top`): a cell focused by a backward Tab is then scrolled clear of the head instead of
    // under it. React runs the returned cleanup as the head unmounts.
    const observeHeadHeight = useCallback((thead: HTMLTableSectionElement | null) => {
        const scroller = thead?.closest<HTMLElement>(`.${styles.scrollContainer}`);
        if (!thead || !scroller) return;
        const write = () => scroller.style.setProperty('--dt-head-height', `${thead.offsetHeight}px`);
        write();
        const observer = new ResizeObserver(write);
        observer.observe(thead);
        return () => observer.disconnect();
    }, []);

    // Compose the container callback ref: capture the node for the virtualizer's getScrollElement
    // and forward it to the column-layout hook (writes --utw-* vars, observes width). Scroll
    // restoration is wired separately by <DataTableScrollRestoration> (only when an id is set).
    const setScrollContainerRef = useCallback(
        (node: HTMLDivElement | null) => {
            scrollContainerNodeRef.current = node;
            setScrollContainerNode(node);
            layoutContainerRef(node);
        },
        [layoutContainerRef]
    );

    // Hand the container to the caller from an effect on the node STATE rather than from the
    // callback ref above: a caller passing an inline function would otherwise change the ref's
    // identity on every render, and React detaches and re-attaches a callback ref whose identity
    // changed — remounting the virtualizer's scroll element on each pass.
    // The cleanup matters as much as the call: an empty result unmounts the table while the list
    // chrome around it stays, and a caller left holding the detached container would keep reading a
    // scroller that can no longer move.
    useEffect(() => {
        onScrollElementChange?.(scrollContainerNode);
        return () => onScrollElementChange?.(null);
    }, [scrollContainerNode, onScrollElementChange]);

    // Column drag-reorder — opt-in per table (enableColumnReorder), requires a persisted layout,
    // disabled on mobile. The DRAGGABLE set is the visible leaf columns that are NOT sticky,
    // fixed-width or lock-visible — those keep their slots so the sticky prefix can't be broken.
    const isMobile = useIsMobile();
    const reorderEnabled = hasLayoutPersistence && !isMobile && enableColumnReorder;
    const visibleColumnIds = useMemo(() => visibleLeafColumns.map((column) => column.id), [visibleLeafColumns]);
    const nonDraggableIds = useMemo(() => {
        const ids = new Set<string>();
        for (const column of visibleLeafColumns) {
            const meta = readMeta(column.columnDef.meta);
            if (meta.sticky || meta.fixedPx !== undefined || meta.lockVisible || stickyIds.has(column.id)) {
                ids.add(column.id);
            }
        }
        return ids;
    }, [visibleLeafColumns, stickyIds]);

    // Per-column minimum-width FLOORS, measured from the rendered DOM and fed to the layout hook as
    // `extraMinPx` so content the weight-derived width can't fit is never clipped. Two sources,
    // maxed per column:
    //  - HEADER: every leaf header shows its title on ONE line (headers are nowrap), with
    //    room for the drag grip that fades in on hover for reorderable columns — so the column never
    //    shrinks far enough to clip/wrap the title. The title wrapper is inline-flex (content-width
    //    regardless of the cell width), so the measure is intrinsic + resolution-aware, not a magic
    //    px value.
    //  - TOTALS (only when shown + loaded): the formatted totals value, which can be wider than the
    //    header, must fit too.
    // A shallow compare makes the state update a no-op once widths settle, so there is no
    // measure⇄resolve loop (the measured widths are intrinsic — independent of the resolved width).
    useLayoutEffect(() => {
        const container = scrollContainerNodeRef.current;
        if (!container) {
            setColumnMinFloorPx((prev) => (Object.keys(prev).length === 0 ? prev : {}));
            return;
        }
        const next: Record<string, number> = {};
        const bump = (id: string, px: number) => {
            if (px > (next[id] ?? 0)) next[id] = px;
        };
        const horizontalPadding = (element: HTMLElement): number => {
            const cs = getComputedStyle(element);
            return parseFloat(cs.paddingLeft) + parseFloat(cs.paddingRight);
        };
        // Header floor: one-line title width. The drag grip overlays the title (absolute ⇒ no
        // layout width), so no reserve is added for it.
        container.querySelectorAll<HTMLElement>('thead th[data-col-id]').forEach((th) => {
            const id = th.getAttribute('data-col-id');
            const content = th.firstElementChild;
            if (!id || !content) return;
            const contentWidth = content.getBoundingClientRect().width;
            if (contentWidth <= 0) return;
            bump(id, Math.ceil(contentWidth + horizontalPadding(th)));
        });
        // Totals floor: the formatted value's intrinsic width (a Range over the cell contents is
        // independent of the cell's current/clipped width).
        if (showTotalsRow && !isLoadingTotals) {
            container.querySelectorAll<HTMLElement>('[data-totals-col-id]').forEach((cell) => {
                const id = cell.getAttribute('data-totals-col-id');
                if (!id) return;
                const range = document.createRange();
                range.selectNodeContents(cell);
                const contentWidth = range.getBoundingClientRect().width;
                range.detach();
                if (contentWidth <= 0) return;
                bump(id, Math.ceil(contentWidth + horizontalPadding(cell)));
            });
        }
        setColumnMinFloorPx((prev) => (shallowEqualNumericRecord(prev, next) ? prev : next));
        // Deps exclude columnMinFloorPx + containerWidth (neither is read here): re-measuring on the
        // floor it just set would loop, and the measured widths are intrinsic (independent of the
        // resolved column width). Re-measures only when the header/totals content, columns, or
        // reorder/draggability change.
    }, [showTotalsRow, isLoadingTotals, totals, visibleLeafColumns, reorderEnabled, nonDraggableIds]);

    // Full current order over ALL columns (incl. hidden) a drop is computed against. When the
    // controlled `columnOrder` prop drives the instance it is authoritative; the fallback keeps
    // in-session reorder working for a table without a controlling owner.
    const currentColumnOrder = useMemo(
        () => (columnOrder && columnOrder.length ? columnOrder : visibleColumnIds),
        [columnOrder, visibleColumnIds]
    );
    const {
        sensors: reorderSensors,
        sortableIds,
        onDragStart: onColumnDragStart,
        onDragEnd: onColumnDragEnd,
        onDragCancel: onColumnDragCancel,
        collisionDetection: reorderCollisionDetection,
    } = useDataTableReorder({
        enabled: reorderEnabled,
        visibleColumnIds,
        nonDraggableIds,
        currentOrder: currentColumnOrder,
        // The table instance's own setter is the single commit channel. With controlled `columnOrder`
        // it routes to the owner's onColumnOrderChange (→ saveOrder) via the bridge in useReactTable
        // above; uncontrolled it updates the instance's own state.
        setColumnOrder: table.setColumnOrder,
    });

    // DragOverlay — the dragged column is shown as an opaque clone floating in a portal (out of the
    // scroll container, so following the pointer can't inflate scrollWidth — which is why
    // auto-scroll is safe), while its original cells dim in place. On drag start we measure the
    // column's width + its header / per-row heights from the live DOM so the clone lines up. This
    // state lives in component render scope (OUTSIDE the memoized TableBody) so pickup/drop only
    // mounts/unmounts the overlay without recomputing the body.
    const tableElRef = useRef<HTMLTableElement | null>(null);

    // Measured in the commit a row's expanded block unmounts, so the rows below move before paint.
    // Only this table's own rows: a nested table's rows belong to its own virtualizer.
    const remeasureRows = useCallback(() => {
        tableElRef.current
            ?.querySelectorAll<HTMLTableRowElement>(':scope > tbody > tr[data-index]')
            .forEach((row) => virtualizer.measureElement(row));
    }, [virtualizer]);
    const [draggedColumnId, setDraggedColumnId] = useState<string | null>(null);
    const [overlayMeasure, setOverlayMeasure] = useState<{
        width: number;
        headerHeight: number;
        rowHeights: number[];
        // The row-model index of each measured (viewport) cell, read from its windowed
        // <tr data-index>: the DOM only holds the windowed rows, so a cell's DOM position says
        // nothing about its row-model index.
        rowIndices: number[];
        // Measured totals-cell height for this column when the totals row is shown, so the overlay
        // clone includes the totals value (between header and rows). 0/absent ⇒ no totals row.
        totalsHeight: number;
    } | null>(null);

    const handleColumnDragStart = useCallback(
        (event: DragStartEvent) => {
            onColumnDragStart();
            const id = String(event.active.id);
            const table = tableElRef.current;
            if (table) {
                // Scope the query to THIS column's cells: the result is in DOM order — the <thead>
                // cell first, then the <tbody> cells — so no post-filter is needed. `CSS.escape`
                // guards against ids with selector-special chars.
                const [headerCell, ...bodyCells] = Array.from(
                    table.querySelectorAll<HTMLElement>(`[data-col-id="${CSS.escape(id)}"]`)
                );
                if (headerCell) {
                    // Clone ONLY the rows visible in the viewport into the overlay. The overlay
                    // floats over the visible table and there is no vertical auto-scroll during a
                    // column drag, so off-screen rows would render their (expensive) cells for
                    // nothing — cloning all of them is what made pickup stutter, and it scaled with
                    // row count ("load more"). Body cells are top→bottom, so once one starts below
                    // the viewport every later row is too — break instead of measuring the whole
                    // window.
                    const viewportH = window.innerHeight;
                    const rowHeights: number[] = [];
                    const rowIndices: number[] = [];
                    for (const cell of bodyCells) {
                        const rect = cell.getBoundingClientRect();
                        if (rect.top >= viewportH) break; // below the fold; all later rows are too
                        if (rect.bottom <= 0) continue; // above the fold; skip without cloning
                        const domIndex = cell.closest('tr')?.getAttribute('data-index');
                        if (domIndex == null) continue;
                        rowHeights.push(cell.offsetHeight);
                        rowIndices.push(Number(domIndex));
                    }
                    // Measure this column's totals cell (if the totals row is shown) so the overlay
                    // clone carries the total too — it lives between the header and the rows.
                    const totalsCell = table.querySelector<HTMLElement>(`[data-totals-col-id="${CSS.escape(id)}"]`);
                    setOverlayMeasure({
                        width: headerCell.offsetWidth,
                        headerHeight: headerCell.offsetHeight,
                        rowHeights,
                        rowIndices,
                        totalsHeight: totalsCell?.offsetHeight ?? 0,
                    });
                }
            }
            setDraggedColumnId(id);
        },
        [onColumnDragStart]
    );
    const handleColumnDragEnd = useCallback(
        (event: DragEndEvent) => {
            // Commit synchronously: dnd-kit's drop animation reads the dragged header's SETTLED
            // position to know where to glide the clone, so the reorder must be applied (and the
            // header in its new slot) before that measurement. Clearing the overlay state hands the
            // clone to the DragOverlay's AnimationManager, which snapshots it and runs the glide
            // (REORDER_DROP_ANIMATION). A deferred commit would leave the header in its old slot and
            // the clone would glide to the wrong place.
            onColumnDragEnd(event);
            setDraggedColumnId(null);
            setOverlayMeasure(null);
        },
        [onColumnDragEnd]
    );
    const handleColumnDragCancel = useCallback(
        (_event: DragCancelEvent) => {
            onColumnDragCancel();
            setDraggedColumnId(null);
            setOverlayMeasure(null);
        },
        [onColumnDragCancel]
    );
    // The dragged column's leaf header (for the overlay clone). Read OUTSIDE the memoized body.
    const draggedHeader = useMemo(() => {
        if (!draggedColumnId) return undefined;
        const leafGroup = headerGroups[headerGroups.length - 1];
        return leafGroup?.headers.find((header) => header.column.id === draggedColumnId);
    }, [draggedColumnId, headerGroups]);
    // Viewport-visible rows for the clone, picked by the measured row-model indices. `table` is the
    // only closed-over value besides `overlayMeasure`; the v8 row model is referentially stable until
    // data changes, so re-deriving on each overlayMeasure is the intended trigger.
    // Indexed by `rowIndices` (not a contiguous slice): only the windowed rows exist in the DOM, so
    // their row-model indices are not 0-based.
    const overlayRows = useMemo(() => {
        if (!overlayMeasure) return [];
        const modelRows = table.getRowModel().rows;
        return overlayMeasure.rowIndices.map((rowIndex) => modelRows[rowIndex]).filter((row) => row !== undefined);
    }, [overlayMeasure, table]);

    // --- header cell: right-edge resize handle + SortableHeaderCell for draggable leaf headers. ---
    const renderHeaderCell = (header: Header<DataType, unknown>, isLeafRow: boolean): ReactNode => {
        const column = header.column;
        const meta = readMeta<TotalsType>(column.columnDef.meta);
        const isSticky = stickyIds.has(column.id);
        const canSort = column.getCanSort();
        const sorted = column.getIsSorted();
        // The column's name in plain text, for its grip and its resize handle: the column picker's own
        // label, as `header` is often a render function.
        const columnLabel = plainColumnLabel(meta, column.columnDef.header, column.id);
        // The head cell is named by its title alone: its grip and its resize handle carry names of their own,
        // which would otherwise be read out as part of every cell's column header.
        // By depth and index, not `header.id`: a column's id may hold a space, which would split the reference.
        const titleId = header.isPlaceholder ? undefined : `${tableId}-${header.depth}-${header.index}`;
        // a11y: expose the sort state on the header cell, and a shared toggle handler used by both
        // the click and the keyboard path. `aria-sort` is undefined for non-sortable columns.
        const ariaSort: AriaAttributes['aria-sort'] = !canSort
            ? undefined
            : sorted === 'asc'
              ? 'ascending'
              : sorted === 'desc'
                ? 'descending'
                : 'none';
        const sortHandler = canSort ? column.getToggleSortingHandler() : undefined;
        // Resize handle slot — opt-in per table (enableColumnResize), on every leaf header row cell
        // that is resizable: not fixed-px (frozen width), not explicitly disableResize. The LAST
        // column is included (dragging its right edge re-flows the weighted columns / overflows into
        // the horizontal scroll, same as any other); there is no "skip last column" gate for our
        // weighted layout. Rendered as a sibling of the title inside BOTH header paths (the plain
        // <th> below AND the SortableHeaderCell), so resize and reorder coexist on the same leaf header.
        const resizeHandleSlot: ReactNode =
            hasLayoutPersistence &&
            enableColumnResize &&
            isLeafRow &&
            meta.fixedPx === undefined &&
            !meta.disableResize ? (
                <DataTableColumnResizeHandle
                    columnId={column.id}
                    label={columnLabel}
                    value={resizeValues[column.id]}
                    onResizeStart={onResizeStart}
                    onResizeMove={onResizeMove}
                    onResizeEnd={onResizeEnd}
                    onResizeReset={onResizeReset}
                    onResizeBy={onResizeBy}
                />
            ) : null;
        // The rendered title + sort caret. Extracted so it is byte-identical between the plain <th>
        // and the SortableHeaderCell path (the markup lives in one place).
        const headerContent: ReactNode = (
            <>
                {header.isPlaceholder ? null : (
                    // Truncate a long header with an ellipsis instead of letting it push the sort
                    // caret out of the cell. Inert unless the flex row is width-bounded: only the
                    // draggable SortableHeaderCell caps its row (max-w-full), so it squeezes the
                    // title when the grip gutter opens; the plain header path is unbounded, so this
                    // renders identically there.
                    <span id={titleId} className="min-w-0 truncate">
                        {flexRender(column.columnDef.header, header.getContext())}
                    </span>
                )}
                {canSort ? (
                    // Inactive carets rest at secondary.light (orange); the active direction switches
                    // to text.secondary (grey, == --color-fg-secondary).
                    // shrink-0 keeps the caret full-size and inside the cell while the title
                    // truncates beside it.
                    <span
                        aria-hidden
                        data-sort-caret
                        className="inline-flex shrink-0 flex-col text-[0.75rem] leading-none"
                    >
                        <span className={cn(sorted === 'asc' ? 'text-fg-secondary' : 'text-secondary-light')}>▲</span>
                        <span
                            className={cn('-mt-0.5', sorted === 'desc' ? 'text-fg-secondary' : 'text-secondary-light')}
                        >
                            ▼
                        </span>
                    </span>
                ) : null}
            </>
        );

        // Drag-reorder is limited to the leaf-row, non-sticky/fixed/locked headers; everything else
        // stays a plain cell that can't be dragged or displaced — which preserves the sticky prefix
        // by construction.
        if (reorderEnabled && isLeafRow && !nonDraggableIds.has(column.id)) {
            return (
                <DataTableSortableHeaderCell
                    key={header.id}
                    columnId={column.id}
                    label={columnLabel}
                    titleId={titleId}
                    isSticky={isSticky}
                    isLastSticky={isSticky && column.id === lastStickyId}
                    textAlign={meta.textAlign}
                    canSort={canSort}
                    onSortToggle={sortHandler}
                    ariaSort={ariaSort}
                    resizeHandleSlot={resizeHandleSlot}
                    widthVar={columnWidthVar(column.id)}
                >
                    {headerContent}
                </DataTableSortableHeaderCell>
            );
        }

        return (
            <th
                key={header.id}
                role="columnheader"
                data-col-id={column.id}
                data-sticky={isSticky || undefined}
                data-sticky-last={(isSticky && column.id === lastStickyId) || undefined}
                aria-sort={ariaSort}
                aria-labelledby={titleId}
                style={{
                    width: `var(${columnWidthVar(column.id)})`,
                    // The cell is a flex box, so the title is positioned by justification, not by
                    // text-align (which the inline-flex title wrapper below would ignore anyway).
                    justifyContent: justifyFor(meta.textAlign),
                    ...(isSticky ? { left: `var(${stickyLeftVar(column.id)})` } : {}),
                }}
            >
                {/* When sortable, the title is a focusable button-like control so keyboard users can
                    trigger the sort (Enter/Space); aria-sort on the <th> exposes the state. */}
                <div
                    className={cn(
                        'inline-flex items-center gap-[9.2px] whitespace-nowrap',
                        canSort && 'cursor-pointer select-none'
                    )}
                    style={meta.textAlign ? { textAlign: meta.textAlign } : undefined}
                    {...(canSort
                        ? {
                              role: 'button',
                              tabIndex: 0,
                              onClick: sortHandler,
                              onKeyDown: (event: KeyboardEvent<HTMLDivElement>) => {
                                  if (event.key === 'Enter' || event.key === ' ') {
                                      event.preventDefault();
                                      sortHandler?.(event);
                                  }
                              },
                          }
                        : {})}
                >
                    {headerContent}
                </div>
                {isLeafRow ? resizeHandleSlot : null}
            </th>
        );
    };

    // --- totals row: a bold sticky row directly under the header ---
    // With `animateTotalsRow` the row slides: `in` from under the header as it appears, `out` back under
    // it before it goes, kept mounted until then. The rows move in step by its height, which is measured
    // once per slide into a variable the keyframes read. A card that ends after its rows grows or shrinks
    // by that height at once, so its bottom edge is clipped back to where the rows are drawn and follows
    // them (`data-totals-card`); a card that scrolls keeps its edge, and takes no clip.
    const [totalsSlide, setTotalsSlide] = useState<'in' | 'out' | null>(null);
    const [totalsRowShown, setTotalsRowShown] = useState(showTotalsRow);
    if (showTotalsRow !== totalsRowShown) {
        setTotalsRowShown(showTotalsRow);
        const reducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;
        setTotalsSlide(animateTotalsRow && !reducedMotion ? (showTotalsRow ? 'in' : 'out') : null);
    }
    useEffect(() => {
        if (!totalsSlide) return;
        const timer = setTimeout(() => setTotalsSlide(null), TOTALS_SLIDE_MS);
        return () => clearTimeout(timer);
    }, [totalsSlide]);
    const totalsRowRef = useRef<HTMLTableRowElement | null>(null);
    useLayoutEffect(() => {
        const container = scrollContainerNodeRef.current;
        const height = totalsSlide ? totalsRowRef.current?.offsetHeight : undefined;
        if (!container || !totalsSlide || !height) {
            container?.removeAttribute('data-totals-card');
            return;
        }
        // How far the card's own bottom edge moves: the row's height when the card fits its rows, none when
        // they overflow it either side of the toggle, the difference when they overflow on one side only.
        const cardShift = Math.min(height, Math.max(0, container.clientHeight - (container.scrollHeight - height)));
        tableElRef.current?.style.setProperty('--dt-totals-h', `${height}px`);
        container.style.setProperty('--dt-card-shift', `${cardShift}px`);
        if (cardShift > 0) container.setAttribute('data-totals-card', totalsSlide);
        else container.removeAttribute('data-totals-card');
    }, [totalsSlide]);

    const totalsRow =
        (showTotalsRow || totalsSlide === 'out') && headerGroups.length > 0 ? (
            <tr ref={totalsRowRef} className={styles.totalsRow} role="row">
                {visibleLeafColumns.map((column, index) => {
                    const meta = readMeta<TotalsType>(column.columnDef.meta);
                    const isSticky = stickyIds.has(column.id);
                    // A draggable totals cell must follow its column during a drag (transform +
                    // drop-edge) so the totals row moves with the displaced columns and shows the
                    // insertion indicator. It deliberately does NOT apply the drag opacity var: the
                    // totals row is sticky-pinned over the scrolling body, so fading it would let body
                    // rows bleed through the pinned row (reads as a transparent totals row). It stays
                    // opaque; the header + body cells still dim into the placeholder. Sticky/locked
                    // totals cells are non-draggable and keep their plain style. Idle ⇒ every var
                    // resolves to its no-op default, so the cell is byte-identical. NOTE: no
                    // `data-col-id` here, so the drag-start measurement query never picks up totals cells.
                    const isDraggable = reorderEnabled && !nonDraggableIds.has(column.id);
                    return (
                        <td
                            key={`totals-${column.id}`}
                            role="cell"
                            // Measured by the totals-floor layout effect so the column is grown to
                            // fit this value. Distinct from `data-col-id` (drag) so the drag-start
                            // cell query never picks up totals cells.
                            data-totals-col-id={column.id}
                            data-sticky={isSticky || undefined}
                            data-sticky-last={(isSticky && column.id === lastStickyId) || undefined}
                            style={{
                                width: `var(${columnWidthVar(column.id)})`,
                                ...(meta.textAlign ? { textAlign: meta.textAlign } : {}),
                                justifyContent: justifyFor(meta.textAlign),
                                ...(isSticky ? { left: `var(${stickyLeftVar(column.id)})` } : {}),
                                ...(isDraggable
                                    ? {
                                          transform: `var(${columnDragXVar(column.id)}, none)`,
                                          transition: `var(${columnDragTransitionVar(column.id)}, none)`,
                                          boxShadow: `var(${columnDropEdgeVar(column.id)}, none)`,
                                      }
                                    : {}),
                            }}
                        >
                            {index === 0
                                ? t('totals.totals')
                                : isLoadingTotals
                                  ? null
                                  : meta.renderTotal && totals !== undefined
                                    ? meta.renderTotal(totals)
                                    : null}
                        </td>
                    );
                })}
            </tr>
        ) : null;

    // --- frozen lead-in row: the group ribbon of the row at the top of the viewport ---
    // It rides in the sticky <thead>, which is what pins it for free — and the only option: a
    // windowed body row is absolutely positioned, so a sticky block on the row itself has nowhere to
    // travel. The row hangs BELOW the header out of its flow (see the stylesheet), so it overlays the
    // in-flow block of the row underneath instead of displacing it. `colSpan` over the visible
    // columns keeps it spanning the table through horizontal scroll; the block paints its own
    // opaque background.
    // `aria-hidden`: it is a second rendering of a block a row already carries, so announcing it
    // would repeat the group — and would read a data cell out inside the column headers. The in-flow
    // block is the one in the accessibility tree while the row carrying it is windowed; scrolled deep
    // enough into a long group that row unmounts and neither copy is left in the tree, which is where
    // the block already stood before it could be frozen. Nothing here is focusable either way.
    const stickyLeadInRow = stickyLeadIn ? (
        <tr className={styles.stickyLeadInRow} role="row" aria-hidden>
            {/* `padding` inline, exactly as the in-flow block's cell sets it: the block paints edge
                to edge, and the shared cell rule's first/last-child 16px inline padding carries a
                `:first-child` on top of its class, so a rule of our own loses to it and leaves a
                16px strip of the row underneath showing down each side. */}
            <td role="cell" colSpan={visibleLeafColumns.length} style={{ width: '100%', padding: 0 }}>
                {stickyLeadIn}
            </td>
        </tr>
    ) : null;

    // The whole table subtree (header + memoized body). The DndContext + DragOverlay wrap this from
    // OUTSIDE (in the same render, below) so pickup/drop — which flips draggedColumnId /
    // overlayMeasure state — mounts/unmounts the overlay WITHOUT recomputing the memoized body.
    const tableNode = (
        // The layout hook's callback ref — it observes the container width, writes the --utw-*
        // variables (column widths, sticky offsets, table min-width) and toggles data-scrolled
        // for the sticky edge shadow. The composed callback ref ALSO captures the node for the
        // virtualizer's getScrollElement (the container is the vertical scroller the window scrolls
        // inside) + the scroll-restoration target.
        <div
            ref={setScrollContainerRef}
            className={cn(styles.scrollContainer, className)}
            // a11y: a scrollable region must be operable by keyboard alone (axe
            // `scrollable-region-focusable`). It matters more now that paging is driven by scroll
            // position: focusing the container and pressing End / PageDown reaches the end of the
            // rows — and therefore the next page — without a pointer. A caller that names it makes
            // that stop a named group; `aria-busy` holds its announcements while rows load.
            tabIndex={0}
            role={ariaLabel ? 'group' : undefined}
            aria-label={ariaLabel}
            aria-busy={isLoading || isFetching}
        >
            {/* Wires scroll restoration only when an id is set, so the router-reading hook is not
                invoked on the no-id path (engine stays usable outside a Router). Renders nothing. */}
            {scrollRestorationId ? (
                <DataTableScrollRestoration scopeKey={scrollRestorationId} containerRef={scrollContainerNodeRef} />
            ) : null}
            {/* The table element ref is used at drag-start to measure the dragged column's cells; it
                composes cleanly with the container ref on the parent div. */}
            <table
                ref={tableElRef}
                className={styles.table}
                data-totals-slide={totalsSlide ?? undefined}
                // The table is a grid/flex stack (rows are absolutely positioned, so the CSS table
                // layout model cannot size them — see the DOM-MODEL block). A `<table>` that is not
                // `display: table` loses its implicit semantics in some engines, so the roles are
                // restated explicitly on every table element.
                role="table"
                // min-width = sum of resolved widths (runtime CSS var) overflows the container into
                // horizontal scroll.
                style={{ minWidth: `var(${TABLE_MIN_WIDTH_VAR})` }}
            >
                {!skipHeader ? (
                    <thead ref={observeHeadHeight} className={styles.thead} role="rowgroup">
                        {(() => {
                            const headerRows = headerGroups.map((headerGroup, groupIndex) => {
                                const isLeafRow = groupIndex === headerGroups.length - 1;
                                return (
                                    <tr key={headerGroup.id} role="row">
                                        {headerGroup.headers.map((header) => renderHeaderCell(header, isLeafRow))}
                                    </tr>
                                );
                            });
                            // Make the leaf header row a dnd-kit sortable set. SortableContext emits
                            // no DOM, so the rows stay direct <thead> children; the surrounding
                            // DndContext + DragOverlay live OUTSIDE the <table> (below) and reach this
                            // SortableContext through React context. The totals row stays out of the set.
                            return reorderEnabled ? (
                                <SortableContext items={sortableIds} strategy={horizontalListSortingStrategy}>
                                    {headerRows}
                                </SortableContext>
                            ) : (
                                headerRows
                            );
                        })()}
                        {totalsRow}
                        {stickyLeadInRow}
                    </thead>
                ) : null}
                {showLoadingSkeleton ? (
                    // Blocking load with no rows yet (initial load / new filter-sort combination, where
                    // the chrome withholds `data`): show the skeleton body instead of an empty table.
                    // Rendered outside the memoized TableBody so it never interacts with the
                    // virtualization / reorder machinery.
                    <DataTableSkeletonBody
                        columnIds={visibleColumnIds}
                        rowCount={skeletonRows}
                        listId={columnConfigId}
                    />
                ) : (
                    <RemeasureRowsContext.Provider value={remeasureRows}>
                        <TableBody<DataType>
                            rows={rows}
                            stripedRows={stripedRows}
                            visibleLeafColumns={visibleLeafColumns}
                            stickyIds={stickyIds}
                            lastStickyId={lastStickyId}
                            onRowClick={onRowClick}
                            getRowHref={getRowHref}
                            onRowNavigate={onRowNavigate}
                            isRowEnabled={isRowEnabled}
                            renderExpandedContent={renderExpandedContent}
                            renderRowLeadIn={renderRowLeadIn}
                            renderFullRow={renderFullRow}
                            isLimited={isLimited}
                            rowEdgeColor={rowEdgeColor}
                            expandedRowEdgeColor={expandedRowEdgeColor}
                            reorderEnabled={reorderEnabled}
                            nonDraggableIds={nonDraggableIds}
                            // `virtualEpoch` is the memo key that re-renders the body on a window move /
                            // load-more / measured-height change — and ONLY then (resize/drag don't move the
                            // window, so the zero-per-frame-re-render invariant is preserved).
                            virtualizer={virtualizer}
                            measureRowRef={measureRowRef}
                            virtualEpoch={virtualEpoch}
                        />
                    </RemeasureRowsContext.Provider>
                )}
            </table>
            {/* Infinite scroll. Deliberately a SIBLING of the table: inside the body it would be a
                windowed row and the virtualizer would unmount it as soon as it scrolled out of the
                window. Renders nothing on the last page. */}
            <EndOfListSentinel
                scrollElement={scrollContainerNode}
                rowCount={rows.length}
                hasNextPage={hasNextPage}
                isLoadingMore={isLoadingMore}
                onLoadMore={onLoadMore}
            />
            {isLoading && data.length > 0 ? (
                <div className={styles.loadingOverlay} aria-busy>
                    <span className="sr-only">Loading</span>
                </div>
            ) : null}
            {isFetching ? <span className="sr-only">Refreshing</span> : null}
        </div>
    );

    // Nothing to draw: the empty state stands in for the table. A caller-supplied node replaces the
    // frame as well, so a page-tier state centres in the pane it was given instead of sitting inside
    // a bordered box; the fallback keeps the frame and shrinks it to the one inline line.
    if (showEmptyState) {
        if (emptyState) return <>{emptyState}</>;
        return (
            <div className={cn(styles.scrollContainer, 'h-auto!', className)}>
                <EmptyState size="inline" />
            </div>
        );
    }

    // When reorder is off, render the table bare (no DnD wiring). When on, wrap it in the DndContext
    // + DragOverlay from OUTSIDE the memoized body so picking up / dropping a column only
    // mounts/unmounts the overlay — the table body element is a stable reference, so React does not
    // re-render it on drag start/end (a recompute on every drag transition would be the pickup/drop
    // lag).
    if (!reorderEnabled) return tableNode;

    return (
        <DndContext
            sensors={reorderSensors}
            collisionDetection={reorderCollisionDetection}
            onDragStart={handleColumnDragStart}
            onDragEnd={handleColumnDragEnd}
            onDragCancel={handleColumnDragCancel}
            modifiers={REORDER_MODIFIERS}
            autoScroll={{ threshold: { x: 0.2, y: 0 } }}
            // dnd-kit's live a11y region is a <div> appended next to the dragged node; in a table
            // that would be an invalid table child, so anchor it on <body>.
            accessibility={{ container: document.body }}
        >
            {tableNode}
            {/* Opaque, outlined clone of the dragged column. On drop it eases into the column's
                committed slot (REORDER_DROP_ANIMATION) instead of vanishing — dnd-kit snapshots its
                own positioned wrapper and glides it to the dragged header's settled position. Lives
                here, OUTSIDE the memoized body. */}
            <DragOverlay
                dropAnimation={reorderOnSpring() ? REORDER_DROP_SPRING : REORDER_DROP_ANIMATION}
                modifiers={REORDER_MODIFIERS}
            >
                {draggedHeader && overlayMeasure ? (
                    <DataTableColumnDragPreview<DataType>
                        header={draggedHeader}
                        rows={overlayRows}
                        width={overlayMeasure.width}
                        headerHeight={overlayMeasure.headerHeight}
                        rowHeights={overlayMeasure.rowHeights}
                        // Include the dragged column's total in the clone when the totals row is
                        // shown (it sits between the header and the rows, as in the table).
                        showTotals={showTotalsRow && overlayMeasure.totalsHeight > 0}
                        totals={totals}
                        totalsHeight={overlayMeasure.totalsHeight}
                    />
                ) : null}
            </DragOverlay>
        </DndContext>
    );
};

/** Detect the project-wide `LIMITED` visibility flag on a row's original DTO. */
const hasLimitedVisibility = (original: unknown): boolean =>
    typeof original === 'object' &&
    original !== null &&
    'visibility' in original &&
    (original as { visibility?: unknown }).visibility === 'LIMITED';

export default DataTable;
